"use client";

/**
 * GameSystemsProvider — top-level orchestrator for cross-cutting game systems.
 *
 * Owns:
 *  1. Title screen gate (visible at startup; hides on New Game / Continue).
 *  2. Audio init on first user pointer interaction (autoplay-policy compliant).
 *  3. Settings (loaded from localStorage; pushed into the audio engine on change).
 *  4. Pause menu (Esc toggles; routes to Inventory / Stats / Dex / Settings / Save / Quit).
 *  5. Inventory overlay.
 *  6. Stats overlay (one party member at a time).
 *  7. Achievement toast queue (serial FIFO drain from achievementTracker.onUnlock).
 *  8. Dialog box overlay (callable from anywhere via useGameSystems().showDialog).
 *  9. BGM swap per scene — subscribed to game-store view + battle.active.
 * 10. Day-night cycle tick (rAF loop calling dayNightCycle.update(dtMs)).
 *
 * Party lives in the zustand game-store party slice (see @/engine/party);
 * inventory remains provider-local. Both are seeded from the save file on
 * Continue and reset to defaults on New Game.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactElement,
  type ReactNode,
} from "react";

import { audio } from "@/engine/audio";
import type { BgmKey } from "@/engine/audio";
import {
  ACHIEVEMENTS,
  achievementTracker,
  type Achievement,
  type AchievementId,
} from "@/engine/achievements";
import { dayNightCycle, type TimeOfDay } from "@/engine/day-night";
import {
  loadGame,
  saveGame,
  SAVE_VERSION,
  type SaveData,
  type SaveSlotId,
} from "@/engine/save-system";
import {
  createPartyMember,
  rehydratePartyMember,
  applyXp,
  type PartyMember,
} from "@/engine/party";
import { applyEvolution } from "@/engine/evolution";
import { getMoveDisplay, type MoveDisplay } from "@/engine/move-learn";
import { resolveSpecies } from "@/engine/battle-participant";

import { useGameStore } from "@/stores/game-store";

import { TitleScreen } from "@/components/game/TitleScreen";
import { PauseMenu } from "@/components/game/PauseMenu";
import {
  SettingsMenu,
  DEFAULT_SETTINGS,
  loadSettings,
  saveSettings,
  type Settings,
} from "@/components/game/SettingsMenu";
import { DialogBox, type DialogPage } from "@/components/game/DialogBox";
import { AchievementToast } from "@/components/game/AchievementToast";
import InventoryScreen from "@/components/game/InventoryScreen";
import StatsScreen, {
  type StatsScreenMember,
} from "@/components/game/StatsScreen";
import { PluginEquipScreen } from "@/components/game/PluginEquipScreen";
import { PluginShop } from "@/components/game/PluginShop";
import {
  SurggieCenter,
  type SurggieCenterMember,
} from "@/components/game/SurggieCenter";
import { EvolutionScreen } from "@/components/game/EvolutionScreen";
import { MoveLearnPrompt } from "@/components/game/MoveLearnPrompt";
import { DelegationPanel } from "@/components/game/DelegationPanel";
import { installPlugin, uninstallPlugin } from "@/engine/plugin-system";
import { PLUGINS } from "@/data/plugins";

// ---------------------------------------------------------------------------
// Public context
// ---------------------------------------------------------------------------

export type GameSystemsContextValue = {
  showDialog: (pages: DialogPage[]) => Promise<string | null>;
  openInventory: () => void;
  openStatsFor: (partyIndex: number) => void;
  openPause: () => void;
  openPluginEquip: () => void;
  openPluginShop: () => void;
  isTitleScreenOpen: boolean;
  isPaused: boolean;
  closeAll: () => void;
  /** Current day-night phase, updated from the rAF tick. */
  dayNightPhase: TimeOfDay;
};

const noopContext: GameSystemsContextValue = {
  showDialog: () => Promise.resolve(null),
  openInventory: () => undefined,
  openStatsFor: () => undefined,
  openPause: () => undefined,
  openPluginEquip: () => undefined,
  openPluginShop: () => undefined,
  isTitleScreenOpen: true,
  isPaused: false,
  closeAll: () => undefined,
  dayNightPhase: "day",
};

export const GameSystemsContext =
  createContext<GameSystemsContextValue>(noopContext);

export function useGameSystems(): GameSystemsContextValue {
  return useContext(GameSystemsContext);
}

// ---------------------------------------------------------------------------
// Internal types
// ---------------------------------------------------------------------------

type DialogRequest = {
  pages: DialogPage[];
  resolve: (choiceId: string | null) => void;
};

// ---------------------------------------------------------------------------
// BGM mapping
// ---------------------------------------------------------------------------

function bgmForScene(opts: {
  view: "overworld" | "department" | "section";
  battleActive: boolean;
  battleIsWild: boolean;
}): BgmKey {
  if (opts.battleActive) {
    return opts.battleIsWild ? "battle_wild" : "battle_trainer";
  }
  switch (opts.view) {
    case "overworld":
      return "overworld";
    case "department":
      return "town";
    case "section":
      return "route";
    default:
      return "overworld";
  }
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatPlayTime(ms: number): string {
  const totalSeconds = Math.floor(ms / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  return `${hours}h ${String(minutes).padStart(2, "0")}m`;
}

function partyFromSave(data: SaveData): PartyMember[] {
  return data.party.map((p) => rehydratePartyMember(p));
}

/** MoveDisplay for a move id, with a visible stub when the catalog misses it
 *  — keeps `currentMoves` index-aligned with `member.moves` for onResolve. */
function moveDisplayOrStub(id: string): MoveDisplay {
  return (
    getMoveDisplay(id) ?? {
      id,
      name: `Move ${id}`,
      type: "normal",
      power: 0,
      accuracy: 0,
      pp: 10,
    }
  );
}

function toStatsMember(p: PartyMember): StatsScreenMember {
  return {
    speciesId: p.speciesId,
    nickname: p.nickname,
    level: p.level,
    xp: p.xp,
    hpCurrent: p.hpCurrent,
    hpMax: p.hpMax,
    moves: p.moves,
    pp: p.pp,
    stats: p.stats,
    types: p.types,
  };
}

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

export function GameSystemsProvider({
  children,
}: {
  children: ReactNode;
}): ReactElement {
  // -- Title / pause / modal gates -----------------------------------------
  const [isTitleScreenOpen, setTitleOpen] = useState(true);
  const [isPaused, setPaused] = useState(false);
  const [isSettingsOpen, setSettingsOpen] = useState(false);
  const [isInventoryOpen, setInventoryOpen] = useState(false);
  const [statsPartyIndex, setStatsPartyIndex] = useState<number | null>(null);
  const [isPluginEquipOpen, setIsPluginEquipOpen] = useState(false);
  const [isPluginShopOpen, setIsPluginShopOpen] = useState(false);
  const [isSurggieCenterOpen, setSurggieCenterOpen] = useState(false);
  const [isDelegationOpen, setIsDelegationOpen] = useState(false);

  // -- Settings ------------------------------------------------------------
  const [settings, setSettings] = useState<Settings>(() => DEFAULT_SETTINGS);

  // -- Party (store-backed; see game-store party slice) ---------------------
  const party = useGameStore((s) => s.party);
  const setParty = useGameStore((s) => s.setParty);
  const updatePartyMember = useGameStore((s) => s.updatePartyMember);
  const healParty = useGameStore((s) => s.healParty);
  const evolutionQueue = useGameStore((s) => s.evolutionQueue);
  const shiftEvolution = useGameStore((s) => s.shiftEvolution);
  const moveLearnQueue = useGameStore((s) => s.moveLearnQueue);
  const shiftMoveLearn = useGameStore((s) => s.shiftMoveLearn);

  // -- Provider-local game data (inventory + play time) ---------------------
  const [inventory, setInventory] = useState<Record<string, number>>({});
  const [ownedPluginIds, setOwnedPluginIds] = useState<Record<string, number>>(
    {},
  );
  const [coins, setCoins] = useState<number>(100);
  const [playTimeMs, setPlayTimeMs] = useState(0);
  const sessionStartRef = useRef<number>(Date.now());

  // -- Dialog --------------------------------------------------------------
  const [dialogRequest, setDialogRequest] = useState<DialogRequest | null>(
    null,
  );

  // -- Achievement toast queue --------------------------------------------
  const [toastQueue, setToastQueue] = useState<AchievementId[]>([]);
  const currentToast: Achievement | null = useMemo(() => {
    const head = toastQueue[0];
    return head ? ACHIEVEMENTS[head] : null;
  }, [toastQueue]);

  // -- Day/night -----------------------------------------------------------
  const [dayNightPhase, setDayNightPhase] = useState<TimeOfDay>(
    dayNightCycle.phase,
  );

  // -- Store subscriptions for BGM swap ------------------------------------
  const view = useGameStore((s) => s.view);
  const battleActive = useGameStore((s) => s.battle.active);
  const battleIsWild = useGameStore((s) => s.battle.isWild);

  // -----------------------------------------------------------------------
  // Load persisted settings on mount
  // -----------------------------------------------------------------------
  useEffect(() => {
    const loaded = loadSettings();
    setSettings(loaded);
    // Push to audio engine (engine has its own load-from-storage but we
    // re-sync to be authoritative).
    audio.setVolumes({
      master: loaded.masterVolume,
      sfx: loaded.sfxVolume,
      bgm: loaded.bgmVolume,
    });
  }, []);

  // -----------------------------------------------------------------------
  // Audio init on first user pointer interaction
  // -----------------------------------------------------------------------
  useEffect(() => {
    if (typeof window === "undefined") return;
    let cancelled = false;

    const onFirstPointer = () => {
      if (cancelled) return;
      void audio.init();
      window.removeEventListener("pointerdown", onFirstPointer);
      window.removeEventListener("keydown", onFirstPointer);
    };

    window.addEventListener("pointerdown", onFirstPointer, { once: false });
    window.addEventListener("keydown", onFirstPointer, { once: false });

    return () => {
      cancelled = true;
      window.removeEventListener("pointerdown", onFirstPointer);
      window.removeEventListener("keydown", onFirstPointer);
    };
  }, []);

  // -----------------------------------------------------------------------
  // Achievement toast subscription — serial drain
  // -----------------------------------------------------------------------
  useEffect(() => {
    const unsubscribe = achievementTracker.onUnlock((id) => {
      setToastQueue((q) => [...q, id]);
    });
    return unsubscribe;
  }, []);

  // -----------------------------------------------------------------------
  // BGM swap per scene
  // -----------------------------------------------------------------------
  useEffect(() => {
    if (isTitleScreenOpen) {
      audio.playBgm("title");
      return;
    }
    const key = bgmForScene({
      view,
      battleActive,
      battleIsWild,
    });
    audio.playBgm(key);
  }, [isTitleScreenOpen, view, battleActive, battleIsWild]);

  // -----------------------------------------------------------------------
  // Day-night rAF tick
  // -----------------------------------------------------------------------
  useEffect(() => {
    if (typeof window === "undefined") return;
    let raf = 0;
    let last = performance.now();

    const tick = (now: number) => {
      const dt = now - last;
      last = now;
      dayNightCycle.update(dt);
      // Cheap equality check via current phase getter.
      const next = dayNightCycle.phase;
      setDayNightPhase((prev) => (prev === next ? prev : next));
      raf = window.requestAnimationFrame(tick);
    };

    raf = window.requestAnimationFrame(tick);
    return () => window.cancelAnimationFrame(raf);
  }, []);

  // -----------------------------------------------------------------------
  // Esc key — open pause / close top modal
  // -----------------------------------------------------------------------
  const anyModalOpen =
    isSettingsOpen ||
    isInventoryOpen ||
    statsPartyIndex !== null ||
    dialogRequest !== null ||
    isPluginEquipOpen ||
    isPluginShopOpen ||
    isSurggieCenterOpen ||
    isDelegationOpen;

  useEffect(() => {
    if (typeof window === "undefined") return;

    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      if (isTitleScreenOpen) return;

      // Surggie Center / Evolution / Move-learn own their Esc handling
      // (onLeave / onCancel / onResolve(null)) — don't double-handle.
      if (
        isSurggieCenterOpen ||
        isDelegationOpen ||
        evolutionQueue.length > 0 ||
        moveLearnQueue.length > 0
      ) {
        return;
      }

      // Close the top modal first (priority: plugin shop/equip > dialog > stats > inventory > settings)
      if (isPluginShopOpen) {
        e.preventDefault();
        setIsPluginShopOpen(false);
        return;
      }
      if (isPluginEquipOpen) {
        e.preventDefault();
        setIsPluginEquipOpen(false);
        return;
      }
      if (dialogRequest) {
        e.preventDefault();
        dialogRequest.resolve(null);
        setDialogRequest(null);
        return;
      }
      if (statsPartyIndex !== null) {
        e.preventDefault();
        setStatsPartyIndex(null);
        return;
      }
      if (isInventoryOpen) {
        e.preventDefault();
        setInventoryOpen(false);
        return;
      }
      if (isSettingsOpen) {
        e.preventDefault();
        setSettingsOpen(false);
        return;
      }

      // Otherwise toggle pause menu.
      e.preventDefault();
      setPaused((p) => !p);
    };

    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [
    isTitleScreenOpen,
    isSettingsOpen,
    isInventoryOpen,
    statsPartyIndex,
    dialogRequest,
    isPluginEquipOpen,
    isPluginShopOpen,
    isSurggieCenterOpen,
    isDelegationOpen,
    evolutionQueue,
    moveLearnQueue,
  ]);

  // -----------------------------------------------------------------------
  // Title screen handlers
  // -----------------------------------------------------------------------
  const handleNewGame = useCallback(() => {
    setParty([createPartyMember("surggie_1", 5)]);
    setInventory({});
    setOwnedPluginIds({});
    setCoins(100);
    setPlayTimeMs(0);
    sessionStartRef.current = Date.now();
    setTitleOpen(false);
  }, [setParty]);

  const handleContinue = useCallback(
    (slotId: SaveSlotId) => {
      const data = loadGame(slotId);
      if (!data) {
        // Bail back to title silently — TitleScreen already filtered empty slots.
        return;
      }
      setParty(partyFromSave(data));
      setInventory(data.inventory);
      setPlayTimeMs(data.playTimeMs);
      sessionStartRef.current = Date.now();
      setTitleOpen(false);
    },
    [setParty],
  );

  const handleTitleSettings = useCallback(() => {
    setSettingsOpen(true);
  }, []);

  // -----------------------------------------------------------------------
  // Settings handlers
  // -----------------------------------------------------------------------
  const handleSettingsChange = useCallback((next: Settings) => {
    setSettings(next);
    saveSettings(next);
    audio.setVolumes({
      master: next.masterVolume,
      sfx: next.sfxVolume,
      bgm: next.bgmVolume,
    });
  }, []);

  // -----------------------------------------------------------------------
  // Pause menu handlers
  // -----------------------------------------------------------------------
  const handlePauseSave = useCallback(async (): Promise<{
    ok: boolean;
    slot?: string;
  }> => {
    // Best-effort save into the auto slot. We synthesize a minimal SaveData
    // from the in-provider state plus the zustand store.
    const store = useGameStore.getState();
    const elapsed = playTimeMs + (Date.now() - sessionStartRef.current);

    const data: SaveData = {
      version: SAVE_VERSION,
      savedAt: Date.now(),
      playTimeMs: elapsed,
      player: {
        x: store.playerPosition.x,
        y: store.playerPosition.y,
        facing: store.playerDirection,
        mapId: store.currentDepartmentId ?? "overworld",
        name: "Player",
      },
      party: party.map((p) => ({
        speciesId: p.speciesId,
        nickname: p.nickname,
        level: p.level,
        xp: p.xp,
        hpCurrent: p.hpCurrent,
        hpMax: p.hpMax,
        moves: p.moves,
        pp: p.pp,
        loadout: p.loadout,
      })),
      pcBox: [],
      inventory,
      progress: {
        defeatedTrainers: [],
        flags: {},
        seenSpecies: [],
        caughtSpecies: [],
      },
    };

    const ok = saveGame("auto", data);
    if (ok) {
      setPlayTimeMs(elapsed);
      sessionStartRef.current = Date.now();
      achievementTracker.onSave();
    }
    return { ok, slot: ok ? "auto" : undefined };
  }, [party, inventory, playTimeMs]);

  const handlePauseLoad = useCallback(() => {
    // Surface load picker via title screen — quit to title.
    setPaused(false);
    setTitleOpen(true);
  }, []);

  const handlePauseParty = useCallback(() => {
    setPaused(false);
    if (party.length > 0) setStatsPartyIndex(0);
  }, [party.length]);

  const handlePauseBag = useCallback(() => {
    setPaused(false);
    setInventoryOpen(true);
  }, []);

  const handlePauseSurggieCenter = useCallback(() => {
    setPaused(false);
    setSurggieCenterOpen(true);
  }, []);

  const handlePauseDex = useCallback(() => {
    setPaused(false);
    // No dedicated Dex screen exists yet — surface a placeholder dialog.
    setDialogRequest({
      pages: [{ text: "Surggie Dex coming soon!" }],
      resolve: () => undefined,
    });
  }, []);

  const handlePauseSettings = useCallback(() => {
    setPaused(false);
    setSettingsOpen(true);
  }, []);

  const handlePauseQuit = useCallback(() => {
    setPaused(false);
    setInventoryOpen(false);
    setStatsPartyIndex(null);
    setSettingsOpen(false);
    setIsPluginEquipOpen(false);
    setIsPluginShopOpen(false);
    setSurggieCenterOpen(false);
    setTitleOpen(true);
  }, []);

  // -----------------------------------------------------------------------
  // Delegation: award XP through the party XP path on completion
  // -----------------------------------------------------------------------
  const handleDelegationComplete = useCallback(
    (partyIndex: number, xp: number) => {
      const member = useGameStore.getState().party[partyIndex];
      if (!member) return;
      const { member: after } = applyXp(member, xp);
      updatePartyMember(partyIndex, after);
    },
    [updatePartyMember],
  );

  // -----------------------------------------------------------------------
  // Inventory: use item
  // -----------------------------------------------------------------------
  const handleUseItem = useCallback(
    async (
      itemId: string,
      _targetPartyIndex?: number,
    ): Promise<{ ok: boolean; message: string }> => {
      // Decrement count; no item-effect engine wired here.
      let used = false;
      setInventory((inv) => {
        const count = inv[itemId] ?? 0;
        if (count <= 0) return inv;
        used = true;
        const next = { ...inv, [itemId]: count - 1 };
        if (next[itemId] === 0) delete next[itemId];
        return next;
      });
      return used
        ? { ok: true, message: "Used." }
        : { ok: false, message: "Nothing happened." };
    },
    [],
  );

  // -----------------------------------------------------------------------
  // Dialog: imperative API via promise
  // -----------------------------------------------------------------------
  const showDialog = useCallback(
    (pages: DialogPage[]): Promise<string | null> => {
      return new Promise<string | null>((resolve) => {
        setDialogRequest({ pages, resolve });
      });
    },
    [],
  );

  const handleDialogChoice = useCallback(
    (choiceId: string) => {
      if (!dialogRequest) return;
      dialogRequest.resolve(choiceId);
      setDialogRequest(null);
    },
    [dialogRequest],
  );

  const handleDialogComplete = useCallback(() => {
    if (!dialogRequest) return;
    dialogRequest.resolve(null);
    setDialogRequest(null);
  }, [dialogRequest]);

  // -----------------------------------------------------------------------
  // Toast dismissal — drain FIFO
  // -----------------------------------------------------------------------
  const handleToastDismiss = useCallback(() => {
    setToastQueue((q) => q.slice(1));
  }, []);

  // -----------------------------------------------------------------------
  // Context value
  // -----------------------------------------------------------------------
  const closeAll = useCallback(() => {
    setPaused(false);
    setSettingsOpen(false);
    setInventoryOpen(false);
    setStatsPartyIndex(null);
    setSurggieCenterOpen(false);
    if (dialogRequest) {
      dialogRequest.resolve(null);
      setDialogRequest(null);
    }
  }, [dialogRequest]);

  const openInventory = useCallback(() => setInventoryOpen(true), []);
  const openStatsFor = useCallback((partyIndex: number) => {
    setStatsPartyIndex(partyIndex);
  }, []);
  const openPause = useCallback(() => setPaused(true), []);
  const openPluginEquip = useCallback(() => setIsPluginEquipOpen(true), []);
  const openPluginShop = useCallback(() => setIsPluginShopOpen(true), []);

  // -----------------------------------------------------------------------
  // Plugin install / uninstall
  // -----------------------------------------------------------------------
  const handlePluginInstall = useCallback(
    async (
      partyIndex: number,
      pluginId: string,
    ): Promise<{ ok: boolean; message: string }> => {
      const member = party[partyIndex];
      if (!member) return { ok: false, message: "No party member." };
      const owned = ownedPluginIds[pluginId] ?? 0;
      if (owned <= 0) return { ok: false, message: "Plugin not owned." };

      const result = installPlugin(member.loadout, pluginId);
      if (!result.ok) {
        const reasonMap: Record<string, string> = {
          not_enough_slots: "Not enough slots.",
          already_installed: "Already installed.",
          plugin_unknown: "Unknown plugin.",
          wrong_species: "Wrong species for this plugin.",
        };
        return {
          ok: false,
          message: reasonMap[result.reason] ?? "Cannot install.",
        };
      }

      updatePartyMember(partyIndex, { loadout: result.loadout });
      setOwnedPluginIds((inv) => {
        const count = inv[pluginId] ?? 0;
        if (count <= 0) return inv;
        const nextCount = count - 1;
        const nextInv = { ...inv };
        if (nextCount <= 0) {
          delete nextInv[pluginId];
        } else {
          nextInv[pluginId] = nextCount;
        }
        return nextInv;
      });
      return { ok: true, message: "Installed." };
    },
    [party, ownedPluginIds, updatePartyMember],
  );

  const handlePluginUninstall = useCallback(
    (partyIndex: number, pluginId: string) => {
      const member = party[partyIndex];
      if (!member) return;
      if (!member.loadout.installedPluginIds.includes(pluginId)) return;

      const nextLoadout = uninstallPlugin(member.loadout, pluginId);
      updatePartyMember(partyIndex, { loadout: nextLoadout });
      setOwnedPluginIds((inv) => ({
        ...inv,
        [pluginId]: (inv[pluginId] ?? 0) + 1,
      }));
    },
    [party, updatePartyMember],
  );

  // -----------------------------------------------------------------------
  // Plugin shop: buy / sell
  // -----------------------------------------------------------------------
  const handlePluginBuy = useCallback(
    async (
      pluginId: string,
    ): Promise<{ ok: boolean; message: string; newBalance?: number }> => {
      const plugin = PLUGINS[pluginId];
      if (!plugin) return { ok: false, message: "Unknown plugin." };
      const price = plugin.buyCoins;
      if (coins < price) {
        return { ok: false, message: "Not enough coins.", newBalance: coins };
      }
      const newBalance = coins - price;
      setCoins(newBalance);
      setOwnedPluginIds((inv) => ({
        ...inv,
        [pluginId]: (inv[pluginId] ?? 0) + 1,
      }));
      return { ok: true, message: `Bought ${plugin.name}.`, newBalance };
    },
    [coins],
  );

  const handlePluginSell = useCallback(
    async (
      pluginId: string,
    ): Promise<{ ok: boolean; message: string; newBalance?: number }> => {
      const plugin = PLUGINS[pluginId];
      if (!plugin) return { ok: false, message: "Unknown plugin." };
      const owned = ownedPluginIds[pluginId] ?? 0;
      if (owned <= 0) {
        return {
          ok: false,
          message: "You do not own that plugin.",
          newBalance: coins,
        };
      }
      const newBalance = coins + plugin.sellCoins;
      setCoins(newBalance);
      setOwnedPluginIds((inv) => {
        const count = inv[pluginId] ?? 0;
        const nextCount = count - 1;
        const nextInv = { ...inv };
        if (nextCount <= 0) {
          delete nextInv[pluginId];
        } else {
          nextInv[pluginId] = nextCount;
        }
        return nextInv;
      });
      return { ok: true, message: `Sold ${plugin.name}.`, newBalance };
    },
    [coins, ownedPluginIds],
  );

  // -----------------------------------------------------------------------
  // Surggie Center / Evolution / Move-learn
  // -----------------------------------------------------------------------
  const handleHealComplete = useCallback(() => {
    healParty();
    setSurggieCenterOpen(false);
  }, [healParty]);

  const handleSurggieCenterLeave = useCallback(() => {
    setSurggieCenterOpen(false);
  }, []);

  const centerParty = useMemo<SurggieCenterMember[]>(
    () =>
      party.map((p) => ({
        speciesId: p.speciesId,
        nickname: p.nickname,
        level: p.level,
        hpCurrent: p.hpCurrent,
        hpMax: p.hpMax,
        fainted: p.fainted,
      })),
    [party],
  );

  // Head of the evolution queue — evolution takes priority over move-learn.
  const evolutionHead = evolutionQueue[0] ?? null;

  const handleEvolutionComplete = useCallback(() => {
    const event = evolutionQueue[0];
    if (event) {
      // EvolutionEvent ids are sprite keys (e.g. "surggie_3"); party speciesIds
      // are sprite keys too. Resolve both through the species dex so either
      // form (sprite key or numeric id) still matches.
      const fromId = resolveSpecies(event.fromSpeciesId)?.id;
      const idx =
        fromId === undefined
          ? -1
          : party.findIndex((m) => resolveSpecies(m.speciesId)?.id === fromId);
      const member = idx >= 0 ? party[idx] : undefined;
      if (member) {
        const evolved = applyEvolution(member, event);
        const newSpecies = resolveSpecies(evolved.speciesId);
        updatePartyMember(idx, {
          ...evolved,
          // Normalize back to the sprite-key convention used by the party.
          speciesId: newSpecies?.sprite ?? evolved.speciesId,
        });
      }
    }
    shiftEvolution();
  }, [evolutionQueue, party, updatePartyMember, shiftEvolution]);

  const moveLearnHead = moveLearnQueue[0] ?? null;
  const learnMember = moveLearnHead
    ? party[moveLearnHead.partyIndex]
    : undefined;
  const learnNewMove = moveLearnHead
    ? getMoveDisplay(moveLearnHead.moveId)
    : null;

  // Drop unfulfillable head requests (bad index / unknown move) so the
  // queue can't soft-lock behind a prompt that will never render.
  useEffect(() => {
    if (!moveLearnHead) return;
    if (!party[moveLearnHead.partyIndex] || !getMoveDisplay(moveLearnHead.moveId)) {
      shiftMoveLearn();
    }
  }, [moveLearnHead, party, shiftMoveLearn]);

  const handleMoveLearnResolve = useCallback(
    (replacedIndex: number | null) => {
      const head = moveLearnQueue[0];
      if (head && replacedIndex !== null) {
        const member = party[head.partyIndex];
        const oldMoveId = member?.moves[replacedIndex];
        if (member && oldMoveId !== undefined) {
          const moves = member.moves.map((mv, i) =>
            i === replacedIndex ? head.moveId : mv,
          );
          const pp = { ...member.pp };
          delete pp[oldMoveId];
          pp[head.moveId] = getMoveDisplay(head.moveId)?.pp ?? 10;
          updatePartyMember(head.partyIndex, { moves, pp });
        }
      }
      shiftMoveLearn();
    },
    [moveLearnQueue, party, updatePartyMember, shiftMoveLearn],
  );

  // Curated shop stock: all common + uncommon plugin ids.
  const shopStock = useMemo<string[]>(
    () =>
      Object.values(PLUGINS)
        .filter((p) => p.rarity === "common" || p.rarity === "uncommon")
        .map((p) => p.id),
    [],
  );

  const contextValue = useMemo<GameSystemsContextValue>(
    () => ({
      showDialog,
      openInventory,
      openStatsFor,
      openPause,
      openPluginEquip,
      openPluginShop,
      isTitleScreenOpen,
      isPaused,
      closeAll,
      dayNightPhase,
    }),
    [
      showDialog,
      openInventory,
      openStatsFor,
      openPause,
      openPluginEquip,
      openPluginShop,
      isTitleScreenOpen,
      isPaused,
      closeAll,
      dayNightPhase,
    ],
  );

  // -----------------------------------------------------------------------
  // Inventory party adapter (PartyMember shape expected by InventoryScreen)
  // -----------------------------------------------------------------------
  const inventoryParty = useMemo(
    () =>
      party.map((p) => ({
        speciesId: p.speciesId,
        nickname: p.nickname,
        level: p.level,
        hpCurrent: p.hpCurrent,
        hpMax: p.hpMax,
        fainted: p.fainted,
      })),
    [party],
  );

  const statsMember =
    statsPartyIndex !== null && party[statsPartyIndex]
      ? toStatsMember(party[statsPartyIndex])
      : null;

  // -----------------------------------------------------------------------
  // Render
  // -----------------------------------------------------------------------
  return (
    <GameSystemsContext.Provider value={contextValue}>
      {children}

      {/* Title screen sits above the canvas until the player enters the game. */}
      {isTitleScreenOpen && (
        <TitleScreen
          onNewGame={handleNewGame}
          onContinue={handleContinue}
          onSettings={handleTitleSettings}
        />
      )}

      {/* Pause menu */}
      <PauseMenu
        open={isPaused && !isTitleScreenOpen && !anyModalOpen}
        onResume={() => setPaused(false)}
        onSave={handlePauseSave}
        onLoad={handlePauseLoad}
        onSettings={handlePauseSettings}
        onParty={handlePauseParty}
        onBag={handlePauseBag}
        onSurggieCenter={handlePauseSurggieCenter}
        onPlugins={() => {
          setPaused(false);
          setIsPluginEquipOpen(true);
        }}
        onDelegate={() => {
          setPaused(false);
          setIsDelegationOpen(true);
        }}
        onDex={handlePauseDex}
        onQuitToTitle={handlePauseQuit}
        playTime={formatPlayTime(
          playTimeMs + (Date.now() - sessionStartRef.current),
        )}
        mapName={useGameStore.getState().currentDepartmentId ?? "Overworld"}
        partyCount={party.length}
      />

      {/* Settings modal */}
      <SettingsMenu
        open={isSettingsOpen}
        settings={settings}
        onChange={handleSettingsChange}
        onClose={() => setSettingsOpen(false)}
      />

      {/* Inventory overlay */}
      <InventoryScreen
        open={isInventoryOpen}
        inventory={inventory}
        party={inventoryParty}
        onUseItem={handleUseItem}
        onClose={() => setInventoryOpen(false)}
      />

      {/* Stats overlay (one party member at a time) */}
      {statsMember && (
        <StatsScreen
          open={statsPartyIndex !== null}
          member={statsMember}
          onClose={() => setStatsPartyIndex(null)}
        />
      )}

      {/* Dialog box overlay */}
      {dialogRequest && (
        <DialogBox
          open
          pages={dialogRequest.pages}
          onChoice={handleDialogChoice}
          onComplete={handleDialogComplete}
        />
      )}

      {/* Plugin equip screen */}
      {isPluginEquipOpen && (
        <PluginEquipScreen
          open={isPluginEquipOpen}
          party={party.map((p) => ({
            speciesId: p.speciesId,
            nickname: p.nickname,
            level: p.level,
            loadout: p.loadout,
          }))}
          ownedPluginIds={ownedPluginIds}
          onInstall={handlePluginInstall}
          onUninstall={handlePluginUninstall}
          onClose={() => setIsPluginEquipOpen(false)}
        />
      )}

      {/* Plugin shop */}
      {isPluginShopOpen && (
        <PluginShop
          open={isPluginShopOpen}
          coins={coins}
          ownedPluginIds={ownedPluginIds}
          inStock={shopStock}
          onBuy={handlePluginBuy}
          onSell={handlePluginSell}
          onClose={() => setIsPluginShopOpen(false)}
        />
      )}

      {/* Surggie Center (heal station) */}
      {isSurggieCenterOpen && (
        <SurggieCenter
          party={centerParty}
          onHealComplete={handleHealComplete}
          onLeave={handleSurggieCenterLeave}
        />
      )}

      {/* Delegation panel — hire a party Surggie to run a real task. */}
      {isDelegationOpen && (
        <DelegationPanel
          open={isDelegationOpen}
          party={party.map((p) => ({
            speciesId: p.speciesId,
            nickname: p.nickname,
            level: p.level,
          }))}
          onComplete={handleDelegationComplete}
          onClose={() => setIsDelegationOpen(false)}
        />
      )}

      {/* Evolution sequence — head of the queue; shows before move-learn. */}
      {!isTitleScreenOpen && evolutionHead && (
        <EvolutionScreen
          event={evolutionHead}
          onComplete={handleEvolutionComplete}
          onCancel={shiftEvolution}
        />
      )}

      {/* Move-learn prompt — only when no evolution is pending. */}
      {!isTitleScreenOpen &&
        !evolutionHead &&
        moveLearnHead &&
        learnMember &&
        learnNewMove && (
          <MoveLearnPrompt
            surggieName={learnMember.nickname?.trim() || learnMember.speciesId}
            newMove={learnNewMove}
            currentMoves={learnMember.moves.map(moveDisplayOrStub)}
            onResolve={handleMoveLearnResolve}
          />
        )}

      {/* Achievement toast (serial FIFO) */}
      <AchievementToast
        achievement={currentToast}
        onDismiss={handleToastDismiss}
      />
    </GameSystemsContext.Provider>
  );
}

export default GameSystemsProvider;
